import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import { apiService } from '@/services/api'
import { AuthCard, AuthAside, AuthLabel, authLinkClass } from '@/components/auth/AuthCard'

// Shown in front of the generated company slug (design system: "the slug is shown in place as a URL")
const COMPANY_URL_PREFIX = 'healthycoach.io/'

const PASSWORD_RULES = [
  { key: 'length', label: '6+ characters' },
  { key: 'uppercase', label: 'Uppercase' },
  { key: 'lowercase', label: 'Lowercase' },
  { key: 'number', label: 'Number' },
]

const SignupPage = () => {
  const [step, setStep] = useState('email') // 'email' | 'details'
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')

  const [formData, setFormData] = useState({
    admin_firstname: '',
    admin_lastname: '',
    company_name: '',
    company_url: '',
    admin_password: '',
    trial_client_limit: 10
  })
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState({})
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    checks: { length: false, uppercase: false, lowercase: false, number: false }
  })

  const { toast } = useToast()
  const navigate = useNavigate()

  // ── Email step ────────────────────────────────────────────────────────────

  const validateEmailFormat = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

  const handleEmailContinue = async (e) => {
    e.preventDefault()
    const trimmed = email.trim()

    if (!trimmed) {
      setEmailError('Email is required')
      return
    }
    if (!validateEmailFormat(trimmed)) {
      setEmailError('Please enter a valid email address')
      return
    }

    setEmailError('')
    setIsCheckingEmail(true)

    const result = await apiService.checkEmailAvailability(trimmed)
    setIsCheckingEmail(false)

    if (!result.success) {
      setEmailError(result.error || 'Could not verify email. Please try again.')
      return
    }

    if (!result.data?.available) {
      setEmailError(result.data?.message || 'This email is already registered. Try signing in instead.')
      return
    }

    setStep('details')
  }

  // ── Details step ──────────────────────────────────────────────────────────

  const toCompanyUrl = (name) =>
    name.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').replace(/-{2,}/g, '-').slice(0, 30)

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData((prev) => {
      const next = { ...prev, [name]: value }
      if (name === 'company_name') {
        next.company_url = toCompanyUrl(value)
      }
      return next
    })

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }

    if (name === 'admin_password') checkPasswordStrength(value)
  }

  const checkPasswordStrength = (password) => {
    const checks = {
      length: password.length >= 6,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password)
    }
    setPasswordStrength({ score: Object.values(checks).filter(Boolean).length, checks })
  }

  const validateDetails = () => {
    const newErrors = {}

    if (!formData.admin_firstname.trim())
      newErrors.admin_firstname = 'First name is required'

    if (!formData.admin_lastname.trim())
      newErrors.admin_lastname = 'Last name is required'

    if (!formData.company_name.trim())
      newErrors.company_name = 'Company name is required'
    else if (formData.company_name.trim().length < 2)
      newErrors.company_name = 'Company name must be at least 2 characters'

    if (!formData.company_url.trim())
      newErrors.company_url = 'Company URL is required'
    else if (formData.company_url.trim().length < 2)
      newErrors.company_url = 'Company URL must be at least 2 characters'
    else if (!/^[a-z0-9-]+$/.test(formData.company_url.trim()))
      newErrors.company_url = 'Only lowercase letters, numbers, and hyphens allowed'
    else if (/^-|-$/.test(formData.company_url.trim()))
      newErrors.company_url = 'URL cannot start or end with a hyphen'

    if (!formData.admin_password)
      newErrors.admin_password = 'Password is required'
    else if (formData.admin_password.length < 6)
      newErrors.admin_password = 'Password must be at least 6 characters'

    if (!formData.trial_client_limit || Number(formData.trial_client_limit) < 1)
      newErrors.trial_client_limit = 'Must be at least 1'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSignup = async (e) => {
    e.preventDefault()
    if (!validateDetails()) return

    setIsSubmitting(true)

    const payload = {
      admin_email: email.trim(),
      admin_firstname: formData.admin_firstname.trim(),
      admin_lastname: formData.admin_lastname.trim(),
      company_name: formData.company_name.trim(),
      company_url: formData.company_url.trim(),
      admin_password: formData.admin_password,
      trial_client_limit: Number(formData.trial_client_limit)
    }

    const result = await apiService.signupCompany(payload)
    setIsSubmitting(false)

    if (result.success) {
      toast({
        title: 'Account created!',
        description: `Welcome to HealthyCoach, ${payload.admin_firstname}! You can now sign in.`,
        variant: 'success'
      })
      navigate('/auth/login')
    } else {
      // Map server-side conflict errors to field-level errors when possible
      const msg = result.error || 'Signup failed. Please try again.'
      if (msg.toLowerCase().includes('company name')) {
        setErrors((prev) => ({ ...prev, company_name: msg }))
      } else if (msg.toLowerCase().includes('company url') || msg.toLowerCase().includes('company_url')) {
        setErrors((prev) => ({ ...prev, company_url: msg }))
      } else if (msg.toLowerCase().includes('email')) {
        setStep('email')
        setEmailError(msg)
      } else {
        toast({ title: 'Signup failed', description: msg, variant: 'destructive' })
      }
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const fieldError = (name) => (errors[name] ? 'border-red-300 focus-visible:border-red-500 focus-visible:ring-red-500/15' : '')
  const errorText = (name) => errors[name] && <p className="mt-1.5 text-xs text-red-600">{errors[name]}</p>

  const renderEmailStep = () => (
    <>
      <AuthCard step={1} totalSteps={2} title="Start your free trial" subtitle="No credit card required.">
        <form onSubmit={handleEmailContinue} noValidate>
          <AuthLabel htmlFor="signup-email">Work email</AuthLabel>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError('') }}
            className={`h-12 ${emailError ? 'border-red-300 focus-visible:border-red-500 focus-visible:ring-red-500/15' : ''}`}
            placeholder="you@yourcompany.com"
            autoFocus
            disabled={isCheckingEmail}
            aria-invalid={Boolean(emailError)}
          />
          {emailError && <p className="mt-1.5 text-xs text-red-600">{emailError}</p>}

          <Button type="submit" size="lg" className="mt-3.5 w-full" disabled={isCheckingEmail}>
            {isCheckingEmail ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking email…</>
            ) : (
              'Continue'
            )}
          </Button>
        </form>
      </AuthCard>

      <AuthAside>
        Already have an account?{' '}
        <Link to="/auth/login" className={authLinkClass}>Sign in</Link>
      </AuthAside>
    </>
  )

  const renderDetailsStep = () => (
    <>
      <AuthCard
        step={2}
        totalSteps={2}
        title="Set up your workspace"
        subtitle={
          <>
            {email} ·{' '}
            <button type="button" onClick={() => { setStep('email'); setErrors({}) }} className={authLinkClass}>
              Change
            </button>
          </>
        }
      >
        <form onSubmit={handleSignup} className="space-y-4" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <AuthLabel htmlFor="admin_firstname">First name</AuthLabel>
              <Input
                id="admin_firstname"
                name="admin_firstname"
                autoComplete="given-name"
                value={formData.admin_firstname}
                onChange={handleChange}
                className={fieldError('admin_firstname')}
                aria-invalid={Boolean(errors.admin_firstname)}
              />
              {errorText('admin_firstname')}
            </div>
            <div>
              <AuthLabel htmlFor="admin_lastname">Last name</AuthLabel>
              <Input
                id="admin_lastname"
                name="admin_lastname"
                autoComplete="family-name"
                value={formData.admin_lastname}
                onChange={handleChange}
                className={fieldError('admin_lastname')}
                aria-invalid={Boolean(errors.admin_lastname)}
              />
              {errorText('admin_lastname')}
            </div>
          </div>

          <div>
            <AuthLabel htmlFor="company_name">Company name</AuthLabel>
            <Input
              id="company_name"
              name="company_name"
              autoComplete="organization"
              value={formData.company_name}
              onChange={handleChange}
              className={fieldError('company_name')}
              placeholder="Acme Fitness"
              aria-invalid={Boolean(errors.company_name)}
            />
            {errorText('company_name')}
          </div>

          <div>
            <AuthLabel htmlFor="company_url">Company URL</AuthLabel>
            <div
              className={`flex h-11 items-center rounded-[10px] border bg-white px-3.5 text-[15px] transition-colors focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/[0.12] ${errors.company_url ? 'border-red-300' : 'border-slate-200'}`}
            >
              <span className="flex-shrink-0 text-slate-500">{COMPANY_URL_PREFIX}</span>
              <input
                id="company_url"
                name="company_url"
                value={formData.company_url}
                onChange={handleChange}
                className="min-w-0 flex-1 bg-transparent text-slate-900 outline-none placeholder:text-slate-500"
                placeholder="acme-fitness"
                aria-invalid={Boolean(errors.company_url)}
              />
            </div>
            {errors.company_url ? (
              errorText('company_url')
            ) : (
              <p className="mt-1.5 text-xs text-slate-500">Generated from your company name. Lowercase, numbers and hyphens.</p>
            )}
          </div>

          <div>
            <AuthLabel htmlFor="admin_password">Password</AuthLabel>
            <div className="relative">
              <Input
                id="admin_password"
                name="admin_password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={formData.admin_password}
                onChange={handleChange}
                className={`pr-16 ${fieldError('admin_password')}`}
                aria-invalid={Boolean(errors.admin_password)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[13px] text-slate-500 transition-colors hover:text-slate-900"
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errorText('admin_password')}

            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {PASSWORD_RULES.map(({ key, label }) => {
                const passed = passwordStrength.checks[key]
                return (
                  <li key={key} className={`flex items-center gap-1 ${passed ? 'text-success' : 'text-slate-500'}`}>
                    <span aria-hidden="true">{passed ? '✓' : '·'}</span>
                    {label}
                    <span className="sr-only">{passed ? ' (met)' : ' (not met)'}</span>
                  </li>
                )
              })}
            </ul>
          </div>

          <div>
            <AuthLabel htmlFor="trial_client_limit">Trial client limit</AuthLabel>
            <Input
              id="trial_client_limit"
              name="trial_client_limit"
              type="number"
              min="1"
              value={formData.trial_client_limit}
              onChange={handleChange}
              className={`w-[110px] ${fieldError('trial_client_limit')}`}
              aria-invalid={Boolean(errors.trial_client_limit)}
            />
            {errors.trial_client_limit
              ? errorText('trial_client_limit')
              : <p className="mt-1.5 text-xs text-slate-500">Maximum clients during your free trial.</p>}
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account…</>
            ) : (
              'Create account'
            )}
          </Button>
        </form>
      </AuthCard>

      <AuthAside>
        Already have an account?{' '}
        <Link to="/auth/login" className={authLinkClass}>Sign in</Link>
      </AuthAside>
    </>
  )

  return <div>{step === 'email' ? renderEmailStep() : renderDetailsStep()}</div>
}

export default SignupPage
